var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { AxiosError } from "axios";
import { DocumentRetrievalLocationStatus, RefreshDirectRecordLocationStatus, } from "healthex-common/document.retrieval.dtos";
import { FASTEN_SEARCH_DEFAULT_LIMIT, FASTEN_SEARCH_ZIPCODE_REGEX, } from "healthex-common/fasten.search.dtos";
import { FASTEN_PLATFORM_ENTITIES } from "healthex-common/fasten.platform.lookup";
import { fullNameForUSAState, USAStates } from "healthex-common/usaStates";
import { enumValues } from "healthex-common/utils";
import { action, computed, observable, runInAction } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { DocumentRetrievalClientFailureStage, logDocumentRetrievalClientError } from "../Infra/logDocumentRetrievalClientError";
import { SearchAnalyticsEvent } from "../Logic/PatientPortalSearchManager";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { SimpleDialogModel } from "./SimpleDialogModel";
const t = getObservableT("patients");
export const STATE_ANY_VALUE = "ANY";
export const STATE_OPTIONS = [STATE_ANY_VALUE, ...enumValues(USAStates)];
export const PLATFORM_ANY_VALUE = "ANY";
export const PLATFORM_OPTIONS = [PLATFORM_ANY_VALUE, ...FASTEN_PLATFORM_ENTITIES.map((entity) => entity.id)];
const PLATFORM_DISPLAY_LABEL_BY_ID = (() => {
    const displayLabelById = new Map();
    for (const entity of FASTEN_PLATFORM_ENTITIES) {
        displayLabelById.set(entity.id, entity.displayValue);
    }
    return displayLabelById;
})();
export var DuplicateDialogChoice;
(function (DuplicateDialogChoice) {
    DuplicateDialogChoice["GO_BACK"] = "GO_BACK";
    DuplicateDialogChoice["REFRESH_EXISTING"] = "REFRESH_EXISTING";
})(DuplicateDialogChoice || (DuplicateDialogChoice = {}));
export class PatientPortalSearchViewModel {
    constructor(opts) {
        this.isModalOpen = false;
        this.searchQuery = "";
        this.showFilters = false;
        this.cityInput = "";
        this.selectedState = STATE_ANY_VALUE;
        this.zipcodeInput = "";
        this.selectedPlatformType = PLATFORM_ANY_VALUE;
        this.appliedCity = "";
        this.appliedState = STATE_ANY_VALUE;
        this.appliedZipcode = "";
        this.appliedPlatformType = PLATFORM_ANY_VALUE;
        this.currentQuery = null;
        this.results = [];
        this.totalHits = 0;
        this.currentOffset = 0;
        this.isLoading = false;
        this.isLoadingMore = false;
        this.hasMoreResults = false;
        this.searchQueryVersion = 0;
        this._searchResultsVersion = 0;
        this.suggestedFastenEndpoints = [];
        this.isSuggestedFastenEndpointsLoading = false;
        this.pendingPortalSelection = null;
        this.pendingSuggestionPortalSelection = null;
        this.isRequestingProvider = false;
        this.failedLogos = {};
        this.duplicateDetectedDialogModel = new SimpleDialogModel();
        this.duplicateDetected = null;
        this.isRefreshingDuplicate = false;
        this.canRefresh = false;
        this.finalizingSelectionKey = null;
        this.parentModel = opts.parentModel;
        this.patientPortalSearchManager = opts.container.patientPortalSearchManager;
        this.documentRetrievalManager = opts.container.documentRetrievalManager;
        this.environment = opts.container.environment;
        this.authManager = opts.container.authManager;
        this.bannerUIManager = opts.container.bannerUIManager;
        this.analyticsService = opts.container.analyticsService;
    }
    setCanRefresh(value) {
        this.canRefresh = value;
    }
    get searchResults() {
        if (this.currentQuery === null) {
            return null;
        }
        return this.results;
    }
    get hasMore() {
        return this.hasMoreResults;
    }
    get searchResultsVersion() {
        return this._searchResultsVersion;
    }
    get shouldShowSuggestedFastenEndpoints() {
        return this.searchResults === null && (this.isSuggestedFastenEndpointsLoading || this.suggestedFastenEndpoints.length > 0);
    }
    get groupedSuggestedFastenEndpoints() {
        const groups = new Map();
        for (const suggestion of this.suggestedFastenEndpoints) {
            const existing = groups.get(suggestion.brandId);
            if (existing) {
                existing.suggestions.push(suggestion);
            }
            else {
                groups.set(suggestion.brandId, {
                    brandId: suggestion.brandId,
                    brandName: suggestion.brandName,
                    suggestions: [suggestion],
                });
            }
        }
        return Array.from(groups.values());
    }
    get shouldShowSearchTips() {
        return this.searchResults === null;
    }
    get shouldShowNoResultsState() {
        return this.searchResults !== null && this.searchResults.length === 0 && !this.isLoading;
    }
    get lastSubmittedQuery() {
        var _a;
        return (_a = this.currentQuery) !== null && _a !== void 0 ? _a : "";
    }
    get dialogTitle() {
        if (this.pendingPortalSelection) {
            return t("Select a portal for {{ providerName }}", {
                providerName: this.pendingPortalSelection.provider.name,
            });
        }
        if (this.pendingSuggestionPortalSelection) {
            return t("Select a portal for {{ providerName }}", {
                providerName: this.pendingSuggestionPortalSelection.brandName,
            });
        }
        return t("Find your records");
    }
    get searchTipItems() {
        return [
            {
                kind: "plain",
                title: t("Try a clinic, hospital or health plan"),
                description: t("e.g. Family Health, Mt. Sinai, United Healthcare"),
            },
            {
                kind: "plain",
                title: t("Try a patient portal"),
                description: t("e.g. MyChart, Healow, FollowMyHealth"),
            },
            {
                kind: "i18nKey",
                titleI18nKey: "Use the filter (<filterIcon/>) to find locations in a specific city or zip code",
                description: t("e.g. San Francisco or 94102"),
            },
        ];
    }
    get noResultsReasonItems() {
        return [
            {
                kind: "plain",
                title: t("Search for a hospital, clinic or network"),
                description: t("HealthEx connects to organizations that host your records, not individual physicians. Try the parent hospital, clinic group or network"),
            },
            {
                kind: "plain",
                title: t("Check your spelling"),
                description: t("Try alternate spellings, or an acronym like NYP, MGH, BCBS"),
            },
            {
                kind: "plain",
                title: t("Use the health system name, not a single clinic"),
                description: t('e.g. search "Mass General Brigham" instead of a specific outpatient office.'),
            },
            {
                kind: "plain",
                title: t("Try a patient portal or EHR name"),
                description: t("Search MyChart, Healow, or FollowMyHealth if you recognize it from your provider. This will not include all locations."),
            },
            {
                kind: "plain",
                title: t("Your provider may not be connected yet"),
                description: t("We connect to 75,000+ sources but coverage is still growing. Request your provider below and we'll notify you when they're live."),
            },
        ];
    }
    get zipcodeError() {
        if (this.zipcodeInput.trim().length === 0) {
            return false;
        }
        return !FASTEN_SEARCH_ZIPCODE_REGEX.test(this.zipcodeInput.trim());
    }
    get effectiveZipcode() {
        const trimmed = this.appliedZipcode.trim();
        if (trimmed.length === 0) {
            return undefined;
        }
        if (!FASTEN_SEARCH_ZIPCODE_REGEX.test(trimmed)) {
            return undefined;
        }
        return trimmed;
    }
    get effectiveCity() {
        const trimmed = this.appliedCity.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    }
    get effectiveState() {
        return this.appliedState === STATE_ANY_VALUE ? undefined : this.appliedState;
    }
    get effectivePlatformType() {
        return this.appliedPlatformType === PLATFORM_ANY_VALUE ? undefined : this.appliedPlatformType;
    }
    get canApplyFilters() {
        return !this.zipcodeError;
    }
    get canResetFilters() {
        return (this.cityInput.trim().length > 0 ||
            this.selectedState !== STATE_ANY_VALUE ||
            this.zipcodeInput.trim().length > 0 ||
            this.selectedPlatformType !== PLATFORM_ANY_VALUE ||
            this.appliedCity.trim().length > 0 ||
            this.appliedState !== STATE_ANY_VALUE ||
            this.appliedZipcode.trim().length > 0 ||
            this.appliedPlatformType !== PLATFORM_ANY_VALUE);
    }
    get hasUnappliedDraftChanges() {
        return (this.cityInput.trim() !== this.appliedCity.trim() ||
            this.selectedState !== this.appliedState ||
            this.zipcodeInput.trim() !== this.appliedZipcode.trim() ||
            this.selectedPlatformType !== this.appliedPlatformType);
    }
    shouldShowMatchedAliasAsTitle(result) {
        return result.matchedVia === "alias" && !!result.matchedAlias;
    }
    getPrimaryDisplayTitle(result) {
        if (this.shouldShowMatchedAliasAsTitle(result) && result.matchedAlias) {
            return result.matchedAlias;
        }
        return result.name;
    }
    getLocationDisplayText(result) {
        const displayLocation = result.displayLocation;
        if (!displayLocation) {
            return undefined;
        }
        if (displayLocation.kind === "many") {
            return t("Multiple Locations");
        }
        if (displayLocation.kind === "single") {
            const { city, state } = displayLocation;
            if (city && state) {
                return `${city}, ${state}`;
            }
            return city !== null && city !== void 0 ? city : state;
        }
        const { totalCount, stateCodes } = displayLocation;
        if (totalCount === undefined || totalCount < 1) {
            return undefined;
        }
        if (!stateCodes || stateCodes.length === 0) {
            return t("locationsWithCount", { count: totalCount });
        }
        const states = stateCodes.join(", ");
        if (stateCodes.length === 1) {
            return t("locationsInState", { count: totalCount, states });
        }
        return t("locationsAcrossStates", { count: totalCount, states });
    }
    getStateOptionLabel(option) {
        if (option === STATE_ANY_VALUE) {
            return t("Any");
        }
        return fullNameForUSAState[option];
    }
    getPlatformOptionLabel(option) {
        var _a;
        if (option === PLATFORM_ANY_VALUE) {
            return t("Any");
        }
        return (_a = PLATFORM_DISPLAY_LABEL_BY_ID.get(option)) !== null && _a !== void 0 ? _a : option;
    }
    hasLogoFailed(brandId) {
        if (!brandId) {
            return false;
        }
        return !!this.failedLogos[brandId];
    }
    markLogoFailed(brandId) {
        this.failedLogos[brandId] = true;
    }
    toggleFilters() {
        if (this.showFilters) {
            this.discardDraftFilters();
        }
        this.showFilters = !this.showFilters;
    }
    discardDraftFilters() {
        this.cityInput = this.appliedCity;
        this.selectedState = this.appliedState;
        this.zipcodeInput = this.appliedZipcode;
        this.selectedPlatformType = this.appliedPlatformType;
    }
    setSearchQuery(query) {
        this.searchQuery = query;
        void this.search(query);
    }
    setCityInput(city) {
        this.cityInput = city;
    }
    setSelectedState(state) {
        this.selectedState = state;
    }
    setZipcodeInput(zipcode) {
        this.zipcodeInput = zipcode;
    }
    setSelectedPlatformType(platformType) {
        this.selectedPlatformType = platformType;
    }
    applyFilters() {
        if (!this.canApplyFilters) {
            return;
        }
        const shouldTriggerSearch = this.hasUnappliedDraftChanges;
        this.appliedCity = this.cityInput;
        this.appliedState = this.selectedState;
        this.appliedZipcode = this.zipcodeInput;
        this.appliedPlatformType = this.selectedPlatformType;
        this.showFilters = false;
        if (shouldTriggerSearch) {
            void this.triggerSearchIfQueryPresent();
        }
    }
    resetFilters() {
        this.clearAllFilterState();
        this.showFilters = false;
        void this.triggerSearchIfQueryPresent();
    }
    clearAllFilterState() {
        this.cityInput = "";
        this.selectedState = STATE_ANY_VALUE;
        this.zipcodeInput = "";
        this.selectedPlatformType = PLATFORM_ANY_VALUE;
        this.appliedCity = "";
        this.appliedState = STATE_ANY_VALUE;
        this.appliedZipcode = "";
        this.appliedPlatformType = PLATFORM_ANY_VALUE;
    }
    async triggerSearchIfQueryPresent() {
        if (this.currentQuery !== null && this.currentQuery.trim().length > 0) {
            await this.search(this.currentQuery);
        }
    }
    async search(query) {
        this.currentQuery = query;
        if (!query || query.trim().length === 0) {
            this.resetResults();
            return;
        }
        const searchVersionAtStart = ++this.searchQueryVersion;
        this.hasMoreResults = false;
        this.isLoading = true;
        try {
            const response = await this.patientPortalSearchManager.search({
                query,
                city: this.effectiveCity,
                state: this.effectiveState,
                zipcode: this.effectiveZipcode,
                platformType: this.effectivePlatformType,
                limit: FASTEN_SEARCH_DEFAULT_LIMIT,
                offset: 0,
            });
            if (this.searchQueryVersion === searchVersionAtStart) {
                runInAction(() => {
                    this.results = response.results;
                    this.totalHits = response.totalHits;
                    this.currentOffset = response.results.length;
                    this.hasMoreResults = response.hasMore && response.results.length > 0;
                    this._searchResultsVersion++;
                });
            }
        }
        finally {
            runInAction(() => {
                if (this.searchQueryVersion === searchVersionAtStart) {
                    this.isLoading = false;
                }
            });
        }
    }
    async loadMore() {
        if (this.isLoadingMore || this.isLoading || !this.hasMoreResults || this.currentQuery === null) {
            return;
        }
        const searchVersionAtStart = this.searchQueryVersion;
        const queryAtStart = this.currentQuery;
        this.isLoadingMore = true;
        try {
            const response = await this.patientPortalSearchManager.loadPage({
                query: queryAtStart,
                city: this.effectiveCity,
                state: this.effectiveState,
                zipcode: this.effectiveZipcode,
                platformType: this.effectivePlatformType,
                limit: FASTEN_SEARCH_DEFAULT_LIMIT,
                offset: this.currentOffset,
            });
            if (this.searchQueryVersion === searchVersionAtStart) {
                runInAction(() => {
                    this.results = [...this.results, ...response.results];
                    this.totalHits = response.totalHits;
                    this.currentOffset += response.results.length;
                    this.hasMoreResults = response.hasMore && response.results.length > 0;
                });
            }
        }
        finally {
            runInAction(() => {
                this.isLoadingMore = false;
            });
        }
    }
    resetResults() {
        this.searchQueryVersion++;
        this._searchResultsVersion++;
        this.currentQuery = null;
        this.results = [];
        this.totalHits = 0;
        this.currentOffset = 0;
        this.hasMoreResults = false;
        this.isLoading = false;
        this.isLoadingMore = false;
    }
    handleOpenModal() {
        var _a;
        this.parentModel.fastenAuthError = null;
        this.parentModel.fastenCustomSearchModalOpen = true;
        this.isModalOpen = true;
        void this.loadSuggestedFastenEndpoints();
        void this.analyticsService.trackHeapEvent({
            event: SearchAnalyticsEvent.SEARCH_MODAL_OPENED,
            identity: (_a = this.authManager.id) !== null && _a !== void 0 ? _a : undefined,
            properties: {
                ...getHeapGlobalEventProperties(),
            },
        });
    }
    handleCloseModal() {
        this.isModalOpen = false;
        this.parentModel.fastenCustomSearchModalOpen = false;
        this.resetForClose();
    }
    resetForClose() {
        this.searchQuery = "";
        this.pendingPortalSelection = null;
        this.pendingSuggestionPortalSelection = null;
        this.failedLogos = {};
        this.suggestedFastenEndpoints = [];
        this.isSuggestedFastenEndpointsLoading = false;
        this.showFilters = false;
        this.clearAllFilterState();
        this.isRequestingProvider = false;
        this.resetResults();
        this.finalizingSelectionKey = null;
        this.duplicateDetected = null;
    }
    async handleResultClick(result) {
        const portalsWithEndpoints = this.portalsWithEndpoints(result);
        if (portalsWithEndpoints.length === 0) {
            console.error("Patient portal search result has no portals with endpoints", { resultId: result.id });
            return;
        }
        if (portalsWithEndpoints.length === 1) {
            await this.finalizePortalSelection(result, portalsWithEndpoints[0]);
            return;
        }
        this.pendingPortalSelection = {
            provider: result,
            portals: portalsWithEndpoints,
        };
    }
    async handlePortalClick(portal) {
        if (!this.pendingPortalSelection) {
            return;
        }
        const committed = await this.finalizePortalSelection(this.pendingPortalSelection.provider, portal);
        if (committed) {
            runInAction(() => {
                this.pendingPortalSelection = null;
            });
        }
    }
    cancelPortalSelection() {
        this.pendingPortalSelection = null;
    }
    async handleRequestProvider() {
        var _a;
        const query = this.lastSubmittedQuery.trim();
        if (!query || this.isRequestingProvider) {
            return;
        }
        const patientId = this.authManager.id;
        if (!patientId) {
            return;
        }
        this.isRequestingProvider = true;
        try {
            await this.patientPortalSearchManager.requestProvider(query, patientId);
            this.bannerUIManager.displayBanner({
                type: "SUCCESS",
                text: t("Request submitted"),
                durationInMS: 3000,
            });
        }
        catch (error) {
            console.error("Failed to submit provider request", error);
            const isRateLimited = error instanceof AxiosError && ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 429;
            this.bannerUIManager.displayBanner({
                type: isRateLimited ? "WARNING" : "ERROR",
                text: isRateLimited
                    ? t("You've made too many requests. Please try again after some time")
                    : t("Something went wrong submitting your request. Please try again."),
                durationInMS: 3000,
            });
        }
        finally {
            runInAction(() => {
                this.isRequestingProvider = false;
            });
        }
    }
    async finalizePortalSelection(result, portal) {
        var _a, _b, _c;
        const endpointId = (_a = portal.endpoints[0]) === null || _a === void 0 ? void 0 : _a.id;
        const platformType = (_b = portal.endpoints[0]) === null || _b === void 0 ? void 0 : _b.platformType;
        if (!endpointId) {
            console.error("Patient portal has no endpoints", { resultId: result.id, portalId: portal.id });
            return false;
        }
        if (this.finalizingSelectionKey !== null) {
            return false;
        }
        const selectionKey = portal.id;
        runInAction(() => {
            this.finalizingSelectionKey = selectionKey;
        });
        try {
            if (!(await this.detectDuplicateAndConfirm({
                name: portal.name,
                alreadyAdded: portal.alreadyAdded,
                addedAt: portal.addedAt,
                brandId: result.id,
                portalId: portal.id,
                endpointId,
                platformType,
            }, selectionKey))) {
                return false;
            }
            void this.analyticsService.trackHeapEvent({
                event: SearchAnalyticsEvent.SEARCH_RESULT_SELECTED,
                identity: (_c = this.authManager.id) !== null && _c !== void 0 ? _c : undefined,
                properties: {
                    ...getHeapGlobalEventProperties(),
                    searchQuery: this.currentQuery,
                    filters: {
                        city: this.effectiveCity,
                        state: this.effectiveState,
                        zipcode: this.effectiveZipcode,
                        platformType: this.effectivePlatformType,
                    },
                    searchResultName: result.name,
                    portalName: portal.name,
                    endpointId,
                    platformType,
                },
            });
            void this.parentModel.providerPortalSelected(result.id, portal.id, endpointId, platformType);
            runInAction(() => {
                this.isModalOpen = false;
                this.searchQuery = "";
            });
            return true;
        }
        finally {
            runInAction(() => {
                if (this.finalizingSelectionKey === selectionKey) {
                    this.finalizingSelectionKey = null;
                }
            });
        }
    }
    async detectDuplicateAndConfirm(candidate, selectionKey) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const locationId = candidate.portalId && candidate.endpointId
            ? this.documentRetrievalManager.findAddedDirectRecordLocationId({
                brandId: candidate.brandId,
                portalId: candidate.portalId,
                endpointId: candidate.endpointId,
            })
            : undefined;
        let isDuplicate = candidate.alreadyAdded === true || locationId !== undefined;
        let addedAt = candidate.addedAt;
        let resolvedLocationId = locationId;
        let existingStatus = this.locationStatusById(locationId);
        const batchId = (_a = this.documentRetrievalManager.retrieval) === null || _a === void 0 ? void 0 : _a.batchId;
        if (batchId) {
            try {
                const existing = await this.documentRetrievalManager.checkIfLocationExists(batchId, {
                    name: (_b = candidate.name) !== null && _b !== void 0 ? _b : undefined,
                    fhirEndpoint: (_c = candidate.fhirEndpoint) !== null && _c !== void 0 ? _c : undefined,
                    hcid: (_d = candidate.hcid) !== null && _d !== void 0 ? _d : undefined,
                    brandId: candidate.brandId,
                    portalId: candidate.portalId,
                    endpointId: candidate.endpointId,
                    platformType: candidate.platformType,
                });
                isDuplicate = isDuplicate || existing.exists;
                addedAt = (_e = existing.addedAt) !== null && _e !== void 0 ? _e : addedAt;
                resolvedLocationId = (_f = existing.locationId) !== null && _f !== void 0 ? _f : resolvedLocationId;
                existingStatus = (_g = existing.status) !== null && _g !== void 0 ? _g : this.locationStatusById(resolvedLocationId);
            }
            catch (e) {
                logDocumentRetrievalClientError("warn", "Duplicate location check failed", {
                    failureStage: DocumentRetrievalClientFailureStage.DUPLICATE_CHECK,
                    batchId,
                    locationId: resolvedLocationId,
                    brandId: candidate.brandId,
                    portalId: candidate.portalId,
                    endpointId: candidate.endpointId,
                    fhirEndpoint: (_h = candidate.fhirEndpoint) !== null && _h !== void 0 ? _h : undefined,
                    platformType: (_j = candidate.platformType) !== null && _j !== void 0 ? _j : undefined,
                    errorMessage: e instanceof Error ? e.message : String(e),
                });
            }
        }
        if (this.finalizingSelectionKey !== selectionKey) {
            return false;
        }
        if (!isDuplicate) {
            return true;
        }
        runInAction(() => {
            var _a;
            this.duplicateDetected = {
                name: (_a = candidate.name) !== null && _a !== void 0 ? _a : "Unknown",
                locationId: resolvedLocationId,
                addedAt,
                status: existingStatus,
            };
        });
        await this.duplicateDetectedDialogModel.showAndWait();
        return false;
    }
    async confirmRefreshDuplicate() {
        if (this.isRefreshingDuplicate) {
            return;
        }
        const didStart = await this.handleRefreshExistingDuplicate();
        if (didStart) {
            runInAction(() => {
                this.duplicateDetectedDialogModel.onChoice(DuplicateDialogChoice.REFRESH_EXISTING);
                this.handleCloseModal();
            });
        }
    }
    get duplicateAddedDateLabel() {
        var _a;
        const addedAt = (_a = this.duplicateDetected) === null || _a === void 0 ? void 0 : _a.addedAt;
        if (!addedAt) {
            return undefined;
        }
        const parsed = new Date(addedAt);
        if (Number.isNaN(parsed.getTime())) {
            return undefined;
        }
        return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
    get canRefreshDuplicate() {
        var _a;
        return this.canRefresh && !!((_a = this.duplicateDetected) === null || _a === void 0 ? void 0 : _a.locationId);
    }
    get needsLoginForDuplicate() {
        var _a;
        const status = (_a = this.duplicateDetected) === null || _a === void 0 ? void 0 : _a.status;
        return status === DocumentRetrievalLocationStatus.SKIPPED || status === DocumentRetrievalLocationStatus.NEEDS_AUTH;
    }
    locationStatusById(locationId) {
        var _a, _b;
        if (locationId === undefined) {
            return undefined;
        }
        return (_b = (_a = this.documentRetrievalManager.retrieval) === null || _a === void 0 ? void 0 : _a.locations.find((location) => location.id === locationId)) === null || _b === void 0 ? void 0 : _b.status;
    }
    async handleRefreshExistingDuplicate() {
        var _a, _b, _c;
        const locationId = (_a = this.duplicateDetected) === null || _a === void 0 ? void 0 : _a.locationId;
        const batchId = (_b = this.documentRetrievalManager.retrieval) === null || _b === void 0 ? void 0 : _b.batchId;
        if (!locationId || !batchId) {
            logDocumentRetrievalClientError("warn", "Duplicate location refresh skipped", {
                failureStage: DocumentRetrievalClientFailureStage.DUPLICATE_REFRESH,
                batchId,
                locationId,
            });
            return false;
        }
        runInAction(() => {
            this.isRefreshingDuplicate = true;
        });
        try {
            const result = await this.documentRetrievalManager.refreshDirectRecordLocation(batchId, locationId);
            if (result.status === RefreshDirectRecordLocationStatus.STARTED) {
                return true;
            }
            if (result.status === RefreshDirectRecordLocationStatus.AUTH_NEEDED) {
                await this.parentModel.startLoginForDuplicateRefresh((_c = result.locationId) !== null && _c !== void 0 ? _c : locationId);
                return true;
            }
            return false;
        }
        catch (error) {
            logDocumentRetrievalClientError("error", "Duplicate location refresh failed", {
                failureStage: DocumentRetrievalClientFailureStage.DUPLICATE_REFRESH,
                batchId,
                locationId,
                errorMessage: error instanceof Error ? error.message : String(error),
            });
            this.bannerUIManager.displayBanner({
                type: "ERROR",
                text: t("An error occurred. Please try again"),
                durationInMS: 3000,
            });
            return false;
        }
        finally {
            runInAction(() => {
                this.isRefreshingDuplicate = false;
            });
        }
    }
    portalsWithEndpoints(result) {
        return result.portals.filter((portal) => portal.endpoints.length > 0);
    }
    hasMultiplePortalsWithEndpoints(result) {
        return this.portalsWithEndpoints(result).length > 1;
    }
    get isFinalizingSelection() {
        return this.finalizingSelectionKey !== null;
    }
    isFinalizingResult(result) {
        const portalsWithEndpoints = this.portalsWithEndpoints(result);
        return portalsWithEndpoints.length === 1 && portalsWithEndpoints[0].id === this.finalizingSelectionKey;
    }
    isFinalizingSuggestionGroup(group) {
        return group.suggestions.length === 1 && group.suggestions[0].endpointId === this.finalizingSelectionKey;
    }
    isPortalAlreadyAdded(portal) {
        return portal.alreadyAdded === true;
    }
    isResultAlreadyAdded(result) {
        const portalsWithEndpoints = this.portalsWithEndpoints(result);
        if (portalsWithEndpoints.length !== 1) {
            return false;
        }
        return this.isPortalAlreadyAdded(portalsWithEndpoints[0]);
    }
    getPortalDisplayUrl(portalWebsite) {
        if (!portalWebsite) {
            return null;
        }
        try {
            return new URL(portalWebsite).hostname;
        }
        catch {
            return portalWebsite;
        }
    }
    async loadSuggestedFastenEndpoints() {
        var _a;
        const batchId = (_a = this.documentRetrievalManager.retrieval) === null || _a === void 0 ? void 0 : _a.batchId;
        if (!batchId) {
            return;
        }
        this.isSuggestedFastenEndpointsLoading = true;
        try {
            const suggestions = await this.documentRetrievalManager.getSuggestedFastenEndpoints(batchId);
            runInAction(() => {
                this.suggestedFastenEndpoints = suggestions;
            });
        }
        catch (error) {
            console.error("Failed to load suggested fasten endpoints", error);
        }
        finally {
            runInAction(() => {
                this.isSuggestedFastenEndpointsLoading = false;
            });
        }
    }
    handleSuggestionGroupClick(group) {
        if (group.suggestions.length === 0) {
            return;
        }
        if (group.suggestions.length === 1) {
            void this.finalizeSuggestionSelection(group.suggestions[0]);
            return;
        }
        this.pendingSuggestionPortalSelection = {
            brandId: group.brandId,
            brandName: group.brandName,
            suggestions: group.suggestions,
        };
    }
    handleSuggestionPortalClick(suggestion) {
        this.pendingSuggestionPortalSelection = null;
        void this.finalizeSuggestionSelection(suggestion);
    }
    cancelSuggestionPortalSelection() {
        this.pendingSuggestionPortalSelection = null;
    }
    hasMultipleSuggestionsInGroup(group) {
        return group.suggestions.length > 1;
    }
    async finalizeSuggestionSelection(suggestion) {
        var _a, _b;
        if (this.finalizingSelectionKey !== null) {
            return;
        }
        const selectionKey = suggestion.endpointId;
        runInAction(() => {
            this.finalizingSelectionKey = selectionKey;
        });
        try {
            if (!(await this.detectDuplicateAndConfirm({
                name: suggestion.portalName,
                brandId: suggestion.brandId,
                portalId: suggestion.portalId,
                endpointId: suggestion.endpointId,
                fhirEndpoint: suggestion.endpointUrl,
                platformType: (_a = suggestion.endpointPlatformType) !== null && _a !== void 0 ? _a : undefined,
            }, selectionKey))) {
                return;
            }
            void this.analyticsService.trackHeapEvent({
                event: SearchAnalyticsEvent.SEARCH_SUGGESTION_SELECTED,
                identity: (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined,
                properties: {
                    ...getHeapGlobalEventProperties(),
                    brandName: suggestion.brandName,
                    brandId: suggestion.brandId,
                    portalId: suggestion.portalId,
                    endpointId: suggestion.endpointId,
                    platformType: suggestion.endpointPlatformType,
                    source: suggestion.source,
                },
            });
            void this.parentModel.suggestedFastenEndpointSelected(suggestion);
            runInAction(() => {
                this.isModalOpen = false;
                this.searchQuery = "";
            });
        }
        finally {
            runInAction(() => {
                if (this.finalizingSelectionKey === selectionKey) {
                    this.finalizingSelectionKey = null;
                }
            });
        }
    }
    getLogoUrl(id) {
        return `${this.environment.FASTEN_LOGO_BASE_URL}/${id}.png`;
    }
}
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "isModalOpen", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "searchQuery", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "showFilters", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "cityInput", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "selectedState", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "zipcodeInput", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "selectedPlatformType", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "appliedCity", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "appliedState", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "appliedZipcode", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "appliedPlatformType", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "currentQuery", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "results", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "totalHits", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "currentOffset", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "isLoading", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "isLoadingMore", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "hasMoreResults", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "_searchResultsVersion", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "suggestedFastenEndpoints", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "isSuggestedFastenEndpointsLoading", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "pendingPortalSelection", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "pendingSuggestionPortalSelection", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "isRequestingProvider", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "failedLogos", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "duplicateDetected", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "isRefreshingDuplicate", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "canRefresh", void 0);
__decorate([
    observable
], PatientPortalSearchViewModel.prototype, "finalizingSelectionKey", void 0);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "setCanRefresh", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "searchResults", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "hasMore", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "searchResultsVersion", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "shouldShowSuggestedFastenEndpoints", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "groupedSuggestedFastenEndpoints", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "shouldShowSearchTips", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "shouldShowNoResultsState", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "lastSubmittedQuery", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "dialogTitle", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "searchTipItems", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "noResultsReasonItems", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "zipcodeError", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "canApplyFilters", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "canResetFilters", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "hasUnappliedDraftChanges", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "markLogoFailed", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "toggleFilters", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "discardDraftFilters", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "setSearchQuery", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "setCityInput", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "setSelectedState", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "setZipcodeInput", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "setSelectedPlatformType", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "applyFilters", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "resetFilters", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "clearAllFilterState", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "search", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "loadMore", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "resetResults", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "handleOpenModal", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "handleCloseModal", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "resetForClose", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "handleResultClick", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "handlePortalClick", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "cancelPortalSelection", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "handleRequestProvider", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "duplicateAddedDateLabel", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "canRefreshDuplicate", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "needsLoginForDuplicate", null);
__decorate([
    computed
], PatientPortalSearchViewModel.prototype, "isFinalizingSelection", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "loadSuggestedFastenEndpoints", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "handleSuggestionGroupClick", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "handleSuggestionPortalClick", null);
__decorate([
    action
], PatientPortalSearchViewModel.prototype, "cancelSuggestionPortalSelection", null);
